import {Tooltip, styled, TooltipProps, IconButton} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';

export const getPlacementBasedStyles = (placement?: string) => {
  if (placement === 'right') {
    return {
      minWidth: '100px',
      paddingTop: '12px',
      right: '10px',
      paddingLeft: '10px',
      top: '0px',
    };
  }
  if (!placement || placement === 'top') {
    return {
      minWidth: '40px',
    };
  }
  return {};
};

export const getArrowBeforeStyles = (
  placement: string | undefined,
  arrowPosition: string | undefined
) => ({
  borderTopRightRadius: '20px',
  left:
    placement === 'top' || placement === undefined
      ? 'calc(50% - 20px)'
      : placement === 'right'
        ? 'calc(50% - 84px)'
        : placement === 'left'
          ? 'calc(100%)'
          : undefined,
  ...(placement === 'right' && {
    top: 'calc(50% - 30px)',
    transform: ' translateX(50%) rotate(90deg); ',
  }),
  ...(placement === 'left' && {
    top: 'calc(50% - 10px)',
    transform: ' translateX(50%) rotate(-90deg); ',
  }),
  ...(arrowPosition === 'right' && {
    right: '20%',
    left: 'calc(76% - 20px)',
    transform: 'translateX(0%)',
  }),
});
export const getArrowAfterStyles = (
  placement: string | undefined,
  arrowPosition: string | undefined
) => ({
  borderTopLeftRadius: '20px',
  left:
    placement === 'top' || placement === undefined
      ? 'calc(50% + 0px)'
      : placement === 'right'
        ? 'calc(50% - 84px)'
        : placement === 'left'
          ? 'calc(100%)'
          : undefined,
  ...(placement === 'right' && {
    top: 'calc(50% - 10px)',
    transform: ' translateX(50%) rotate(90deg); ',
  }),
  ...(placement === 'left' && {
    top: 'calc(50% - 30px)',
    transform: ' translateX(50%) rotate(-90deg); ',
  }),
  ...(arrowPosition === 'right' && {
    right: '20%',
    left: 'calc(75.5% + 0px)',
    transform: 'translateX(0%)',
  }),
});

const AurTooltip = styled(
  ({
    className,
    PointerPlacement,
    toolTipClassName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tooltipSx,
    trigger = 'hover',
    children,
    slotProps,
    ...props
  }: TooltipProps & {
    toolTipClassName?: string;
    arrowPosition?: string;
    tooltipSx?: React.CSSProperties;
    trigger?: 'hover' | 'click';
  } & {
    PointerPlacement?:
      | 'bottom'
      | 'left'
      | 'right'
      | 'top'
      | 'bottom-end'
      | 'bottom-start'
      | 'left-end'
      | 'left-start'
      | 'right-end'
      | 'right-start'
      | 'top-end'
      | 'top-start'
      | undefined;
  }) => {
    /** Ref to anchor element (the element tooltip is attached to) */
    const anchorRef = useRef<any>(null);

    /** Ref to tooltip popper element */
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);

    /**
     * Effect: Handles closing tooltip when clicking outside.
     * Runs only when trigger="click" and tooltip is currently open.
     */
    useEffect(() => {
      if (trigger !== 'click' || !open) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          anchorRef.current?.contains(target) || // click inside anchor
          tooltipRef.current?.contains(target) // click inside tooltip
        ) {
          return; // do nothing
        }
        setOpen(false); // close tooltip
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [open, trigger]);

    const isClickTrigger = trigger === 'click';
    return (
      <Tooltip
        placement={PointerPlacement === undefined ? 'top' : PointerPlacement}
        className={toolTipClassName ?? `typo-text label2_size`}
        {...props}
        classes={{tooltip: className}}
        open={isClickTrigger ? open : undefined}
        disableHoverListener={isClickTrigger}
        onClose={() => setOpen(false)}
        describeChild
        slotProps={{
          ...slotProps,
          popper: {
            ...(slotProps?.popper ?? {}),
            ref: tooltipRef,
          },
        }}
      >
        {trigger === 'click' ? (
          <IconButton
            disabled={props.disableTouchListener}
            aria-label={props['aria-label'] as string}
            ref={anchorRef}
            onClick={
              isClickTrigger ? () => setOpen((prev) => !prev) : undefined
            }
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                setOpen((prev) => !prev);
              }
              if (event.key === 'Escape' && open) {
                event.stopPropagation();
                setOpen(false);
              }
            }}
            data-testid="click-tool-tip"
            style={{
              display: 'inherit',
            }}
            className="custom-icon-button"
          >
            {children}
          </IconButton>
        ) : (
          children
        )}
      </Tooltip>
    );
  }
)(({arrowPosition, PointerPlacement, className, tooltipSx}) => ({
  backgroundColor: 'white',
  color: '#0C1119',
  borderRadius: '5px',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  position: 'relative',
  bottom: '5px',
  padding: '8px',
  minHeight: '40px',
  cursor: 'pointer',
  zIndex: 10,
  ...getPlacementBasedStyles(PointerPlacement),
  justifyContent: 'center',
  alignItems: 'center',
  display: className != 'aur-alert-tooltip' ? 'flex' : 'block',
  textAlign: className != 'aur-alert-tooltip' ? 'center' : 'left',
  maxWidth: '300px',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  ...tooltipSx,
  '&:before, &:after': {
    content: '""',
    position: 'absolute',
    height: '40px',
    width: '20px',
    bottom: '-41px',
    backgroundColor: 'transparent',
    boxShadow: '0 -25px 0 0   #FFFFFF',
    zIndex: -1,
  },

  '&:before': {
    ...getArrowBeforeStyles(PointerPlacement, arrowPosition),
  },

  '&:after': {
    ...getArrowAfterStyles(PointerPlacement, arrowPosition),
  },
}));

export default AurTooltip;
